import { Injectable } from '@angular/core';
import { CoolHttp } from '@angular-cool/http';
import { CkEditorDTO } from '../../../../../common/dto/ck-editor.dto';
import { ApiService } from './api-service';
import { loadCKEditorCloud, type CKEditorCloudResult, type CKEditorCloudConfig } from '@ckeditor/ckeditor5-angular';
import { getCKEditorPlugins } from '../components/ck-editor/Config/ckeditor-plugins';
import { toolbarItems } from '../components/ck-editor/Config/ckeditor-toolbar';
import { exportPdfConfig, exportWordConfig } from '../components/ck-editor/Config/ckeditor-export-config';
import { DEFAULT_HEX_COLORS_CONFIG } from '../components/ck-editor/Config/ckeditor-colors';
import {
  balloonToolbarConfig,
  blockToolbarConfig,
  fullscreenConfig,
  headingsConfig,
  imageConfig,
  stylesConfig,
} from '../components/ck-editor/Config/ckeditor-additional-config';
import type { ClassicEditor, EditorConfig } from 'https://cdn.ckeditor.com/typings/ckeditor5.d.ts';
import { environment } from 'src/environments/environment';

const DEFAULT_HEX_COLORS = DEFAULT_HEX_COLORS_CONFIG;

const cloudConfig = {
  version: '46.0.0',
  ckbox: { version: '2.6.1' },
  premium: true,
} satisfies CKEditorCloudConfig;

@Injectable({
  providedIn: 'root',
})
export class CkEditorService extends ApiService {
  private _ckEditorCloud: CKEditorCloudResult<typeof cloudConfig> | null = null;
  private _licenseKey: string | null = null;
  private _tokenUrl: string | null = null;
  private _isLoading = false;
  private _loadPromise: Promise<CKEditorCloudResult<typeof cloudConfig>> | null = null;

  constructor(private _http: CoolHttp) {
    super();
    // this.preloadCKEditor();
  }

  public async preloadCKEditor(): Promise<CKEditorCloudResult<typeof cloudConfig>> {
    if (this._ckEditorCloud) {
      return this._ckEditorCloud;
    }

    if (this._loadPromise) {
      return this._loadPromise;
    }

    this._isLoading = true;
    this._loadPromise = this._loadCKEditor();

    try {
      this._ckEditorCloud = await this._loadPromise;
      return this._ckEditorCloud;
    } finally {
      this._isLoading = false;
      this._loadPromise = null;
    }
  }

  private async _loadCKEditor(): Promise<CKEditorCloudResult<typeof cloudConfig>> {
    const cloud = await loadCKEditorCloud(cloudConfig);

    try {
      const ckEditorConfig = await this._getCkEditorConfig();
      this._licenseKey = ckEditorConfig.licenseKey || '';
    } catch (error) {
      console.warn('CKEditor credentials not available, using free version:', error);
      this._licenseKey = '';
      this._tokenUrl = '';
    }

    return cloud;
  }

  public getCKEditor(): CKEditorCloudResult<typeof cloudConfig> | null {
    return this._ckEditorCloud;
  }

  public isLoading(): boolean {
    return this._isLoading;
  }

  public isReady(): boolean {
    return this._ckEditorCloud !== null;
  }

  public async createEditorConfig(initialData: string, exportFileBase?: string): Promise<EditorConfig> {
    const cloud = await this.preloadCKEditor();


    return {
      licenseKey: this._licenseKey!,
      cloudServices: {
        tokenUrl: `${environment.serverUrl}/api${this.ServerUrls.CkEditor.children.Get.absoluteUrl}/token`,
        uploadUrl: `${environment.serverUrl}/api${this.ServerUrls.CkEditor.children.Get.absoluteUrl}/upload`,
      },
      toolbar: {
        items: toolbarItems,
        shouldNotGroupWhenFull: true,
      },
      plugins: getCKEditorPlugins(cloud.CKEditor, cloud.CKEditorPremiumFeatures),
      exportPdf: {
        ...exportPdfConfig,
        converterUrl: 'https://pdf-converter.cke-cs-eu.com/v1/convert',
        fileName: exportFileBase ? `${exportFileBase}.pdf` : exportPdfConfig.fileName,
        tokenUrl: `${environment.serverUrl}/api${this.ServerUrls.CkEditor.children.Get.absoluteUrl}/token`, // important
      },
      exportWord: {
        ...exportWordConfig,
        converterUrl: 'https://docx-converter.cke-cs-eu.com/v2/convert/html-docx',
        fileName: exportFileBase ? `${exportFileBase}.docx` : exportWordConfig.fileName,
        tokenUrl: `${environment.serverUrl}/api${this.ServerUrls.CkEditor.children.Get.absoluteUrl}/token`, // important
      },
      importWord: {
        converterUrl: 'https://docx-converter.cke-cs-eu.com/v2/convert/docx-html',
        formatting: {},
      },
      ckbox: {
        serviceOrigin: 'https://api-eu.ckbox.io',
      },
      balloonToolbar: balloonToolbarConfig,
      blockToolbar: blockToolbarConfig,
      fullscreen: fullscreenConfig,
      heading: headingsConfig,
      image: imageConfig,
      initialData: initialData,
      fontBackgroundColor: {
        colorPicker: {
          format: 'hex',
        },
        colors: DEFAULT_HEX_COLORS,
      },
      fontColor: {
        colorPicker: {
          format: 'hex',
        },
        colors: DEFAULT_HEX_COLORS,
      },
      fontFamily: {
        options: [
          // 'default',
          'Inter, sans-serif',
          'Syne, "Helvetica Neue", sans-serif',
        ],
        supportAllValues: true,
      },
      fontSize: {
        options: [
          10,
          12,
          14,
          'default',
          18,
          20,
          22,
        ],
        supportAllValues: true,
      },
      htmlSupport: {
        allow: [
          {
            name: /^(div|table|tbody|tr|td|th|span|img|h1|h2|h3|p|a|ul|li|ol)$/,
            styles: true,
            attributes: true,
            classes: true,
          },
        ],
      },
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: 'https://',
        decorators: {
          toggleDownloadable: {
            mode: 'manual',
            label: 'Downloadable',
            attributes: {
              download: 'file',
            },
          },
        },
      },
      list: {
        properties: {
          styles: true,
          startIndex: true,
          reversed: false,
        },
      },
      mention: {
        feeds: [
          {
            marker: '@',
            feed: [],
          },
        ],
      },
      menuBar: {
        isVisible: true,
      },
      placeholder: 'Type or paste your content here!',
      style: stylesConfig,
      table: {
        contentToolbar: [
          'tableColumn',
          'tableRow',
          'mergeTableCells',
          'tableProperties',
          'tableCellProperties',
        ],
        tableProperties: {
          borderColors: DEFAULT_HEX_COLORS,
          backgroundColors: DEFAULT_HEX_COLORS,
        },
        tableCellProperties: {
          borderColors: DEFAULT_HEX_COLORS,
          backgroundColors: DEFAULT_HEX_COLORS,
        },
      },
    };
  }

  public async _getCkEditorConfig(): Promise<CkEditorDTO> {
    try {
      const config = await this._http.getAsync(this.ServerUrls.CkEditor.children.Get.absoluteUrl);

      return config;
    } catch (error) {
      console.warn('Failed to get CKEditor configuration:', error);
      return { licenseKey: ''};
    }
  }
}
